/**
 * Currency and number formatting utilities
 * Provides reusable functions for consistent currency formatting across the application
 */

/**
 * Maximum safe value for currency inputs.
 * Set to just under Number.MAX_SAFE_INTEGER (9,007,199,254,740,991)
 * to prevent JavaScript precision loss.
 * This equals approximately 9 trillion - more than sufficient for any realistic currency input.
 */
export const MAX_SAFE_CURRENCY = 9007199254740991; // Number.MAX_SAFE_INTEGER

/**
 * Options for formatting numbers
 */
export interface NumberFormatOptions {
  /**
   * Minimum number of decimal places to display
   * @default 2
   */
  minimumFractionDigits?: number;
  /**
   * Maximum number of decimal places to display
   * @default 2
   */
  maximumFractionDigits?: number;
  /**
   * Locale to use for formatting
   * @default 'en-US'
   */
  locale?: string;
}

/**
 * Options for formatting currency
 */
export interface CurrencyFormatOptions extends NumberFormatOptions {
  /**
   * Currency code (e.g., 'USD', 'EUR', 'GBP')
   * @default 'USD'
   */
  currency?: string;
  /**
   * Whether to include the currency symbol
   * @default true
   */
  includeCurrencySymbol?: boolean;
}

/**
 * Formats a number with thousand separators and decimal places.
 *
 * @param value - The number to format
 * @param options - Formatting options
 * @returns Formatted number string (e.g., "4,789.58")
 *
 * @example
 * ```typescript
 * formatNumber(4789.58); // "4,789.58"
 * formatNumber(1234567.89, { minimumFractionDigits: 0 }); // "1,234,568"
 * formatNumber(123.4, { locale: 'de-DE' }); // "123,40"
 * ```
 */
export function formatNumber(
  value: number,
  options: NumberFormatOptions = {}
): string {
  const {
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    locale = 'en-US',
  } = options;

  return value.toLocaleString(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  });
}

/**
 * Formats a number as currency with symbol and proper formatting.
 *
 * @param value - The amount to format
 * @param options - Currency formatting options
 * @returns Formatted currency string (e.g., "$4,789.58")
 *
 * @example
 * ```typescript
 * formatCurrency(4789.58); // "$4,789.58"
 * formatCurrency(4789.58, { currency: 'EUR' }); // "€4,789.58"
 * formatCurrency(4789.58, { includeCurrencySymbol: false }); // "4,789.58"
 * formatCurrency(1234.5, { locale: 'de-DE', currency: 'EUR' }); // "1.234,50 €"
 * ```
 */
export function formatCurrency(
  value: number,
  options: CurrencyFormatOptions = {}
): string {
  const {
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    locale = 'en-US',
    currency = 'USD',
    includeCurrencySymbol = true,
  } = options;

  if (!includeCurrencySymbol) {
    return formatNumber(value, {
      minimumFractionDigits,
      maximumFractionDigits,
      locale,
    });
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
}

/**
 * Formats a balance amount for display in fund components.
 * This is a convenience wrapper around formatNumber with sensible defaults.
 *
 * @param balance - The balance amount to format
 * @param locale - Optional locale (defaults to 'en-US')
 * @returns Formatted balance string (e.g., "4,789.58")
 *
 * @example
 * ```typescript
 * formatBalance(4789.58); // "4,789.58"
 * formatBalance(150.5); // "150.50"
 * ```
 */
export function formatBalance(balance: number, locale = 'en-US'): string {
  return formatNumber(balance, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    locale,
  });
}

/**
 * Formats a currency amount with custom symbol placement.
 * Useful when you want to manually control symbol display (e.g., for custom UI layouts).
 *
 * @param value - The amount to format
 * @param currencySymbol - The currency symbol (e.g., '$', '€', '£')
 * @param options - Number formatting options
 * @returns Object with formatted number and symbol
 *
 * @example
 * ```typescript
 * const { formattedValue, symbol } = formatCurrencyWithSymbol(4789.58, '$');
 * // formattedValue: "4,789.58", symbol: "$"
 * // Use in template: `${symbol}${formattedValue}`
 * ```
 */
export function formatCurrencyWithSymbol(
  value: number,
  currencySymbol: string,
  options: NumberFormatOptions = {}
): { formattedValue: string; symbol: string } {
  return {
    formattedValue: formatNumber(value, options),
    symbol: currencySymbol,
  };
}

/**
 * Convert currency amount using an exchange rate
 *
 * @param amount - The amount to convert
 * @param exchangeRate - The exchange rate (can be string or number)
 * @returns Converted amount
 *
 * @example
 * ```typescript
 * convertCurrency(100, '1.2'); // 120
 * convertCurrency(100, 0.85); // 85
 * convertCurrency(100, 'invalid'); // 100 (returns original on error)
 * ```
 */
export function convertCurrency(
  amount: number,
  exchangeRate: string | number
): number {
  try {
    const rate = typeof exchangeRate === 'string'
      ? parseFloat(exchangeRate)
      : exchangeRate;

    if (isNaN(rate) || rate === 0) {
      console.warn('Invalid exchange rate:', exchangeRate);
      return amount;
    }

    return amount * rate;
  } catch (error) {
    console.error('Error converting currency:', error);
    return amount;
  }
}

/**
 * Parse a formatted currency string to a number.
 * Removes commas and other formatting characters.
 *
 * @param value - Formatted string (e.g., "1,234.56" or "1234.5")
 * @returns Numeric value (e.g., 1234.56) or null if invalid
 *
 * @example
 * ```typescript
 * parseCurrencyInput('1,234.56'); // 1234.56
 * parseCurrencyInput('1234'); // 1234
 * parseCurrencyInput(''); // null
 * parseCurrencyInput('abc'); // null
 * ```
 */
export function parseCurrencyInput(value: string): number | null {
  if (!value || value.trim() === '') return null;

  // Remove commas and spaces
  const cleaned = value.replace(/[,\s]/g, '');

  // Validate format (optional decimal with up to 2 places)
  if (!/^\d+\.?\d{0,2}$/.test(cleaned)) return null;

  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? null : parsed;
}

/**
 * Format input value for display with thousand separators.
 * Preserves partial decimal input during typing.
 *
 * @param value - Raw input value (may include commas from previous formatting)
 * @returns Formatted string for display
 *
 * @example
 * ```typescript
 * formatInputDisplay('1234'); // "1,234"
 * formatInputDisplay('1234.5'); // "1,234.5"
 * formatInputDisplay('1234.'); // "1,234."
 * formatInputDisplay('1,234.56'); // "1,234.56"
 * ```
 */
export function formatInputDisplay(value: string): string {
  if (!value) return '';

  // Split on decimal point
  const parts = value.replace(/,/g, '').split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1];

  // Format integer part with commas
  const formatted = parseInt(integerPart || '0', 10).toLocaleString('en-US', {
    useGrouping: true,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  // Preserve decimal part during typing
  if (decimalPart !== undefined) {
    return `${formatted}.${decimalPart}`;
  }

  // If value ended with decimal point, preserve it
  if (value.endsWith('.')) {
    return `${formatted}.`;
  }

  return formatted;
}

/**
 * Calculate new cursor position after formatting.
 * Accounts for added/removed commas to maintain cursor at the correct position.
 *
 * @param oldValue - Value before formatting
 * @param newValue - Value after formatting
 * @param oldPosition - Cursor position before formatting
 * @returns New cursor position
 *
 * @example
 * ```typescript
 * // User types "4" in "1,234" to make "1,2344"
 * // After formatting: "12,344"
 * calculateCursorPosition('1,2344', '12,344', 6); // Returns 6
 * ```
 */
export function calculateCursorPosition(
  oldValue: string,
  newValue: string,
  oldPosition: number
): number {
  // Count non-comma characters before cursor in old value
  const beforeCursor = oldValue.substring(0, oldPosition);
  const digitsBeforeCursor = beforeCursor.replace(/,/g, '').length;

  // Find position in new value with same digit count
  let digitCount = 0;
  let newPosition = 0;

  for (let i = 0; i < newValue.length; i++) {
    if (newValue[i] !== ',') {
      digitCount++;
    }
    if (digitCount === digitsBeforeCursor) {
      newPosition = i + 1;
      break;
    }
  }

  // If we didn't find the position, place cursor at end
  if (newPosition === 0 && digitsBeforeCursor > 0) {
    newPosition = newValue.length;
  }

  return newPosition;
}

/**
 * Validates that a numeric value doesn't exceed JavaScript's safe integer limit.
 * Returns true if the value is safe to use without precision loss.
 *
 * @param value - The numeric value to check (can be number or string)
 * @returns true if value is within safe range, false otherwise
 *
 * @example
 * ```typescript
 * isValueWithinSafeRange(1234567.89); // true
 * isValueWithinSafeRange('9999999999999'); // false (exceeds MAX_SAFE_CURRENCY)
 * isValueWithinSafeRange('1000.50'); // true
 * ```
 */
export function isValueWithinSafeRange(value: number | string): boolean {
  const numericValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  return !isNaN(numericValue) && Math.abs(numericValue) <= MAX_SAFE_CURRENCY;
}
